import { useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Copy, Check } from 'lucide-react'

interface ErrorDoc {
  type: string
  title: string
  status: number
  summary: string
  when_it_happens: string[]
  example_response: string
  how_to_fix: string[]
}

interface RelatedError {
  type: string
  title: string
  status: number
}

const statusColor = (status: number) => {
  if (status >= 500) return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
  if (status >= 400) return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
  return 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20'
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-foreground"
        onClick={copy}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
      <pre className="overflow-x-auto rounded-lg bg-zinc-950 p-4 text-sm leading-relaxed">
        <code className="font-mono text-zinc-300">{code}</code>
      </pre>
    </div>
  )
}

export default function ErrorShow({ error, related }: { error: ErrorDoc; related: RelatedError[] }) {
  return (
    <>
      <Head>
        <title>{`${error.title} (${error.status}) — KonexZero API Errors`}</title>
        <meta name="description" content={error.summary} />
      </Head>
      <PageLayout>
        <Link
          href="/errors"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All errors
        </Link>

        <div className="mb-10 space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={statusColor(error.status)}>
              HTTP {error.status}
            </Badge>
            <code className="font-mono text-sm text-muted-foreground">{error.type}</code>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            {error.title}
          </h1>
          <p className="max-w-2xl text-lg text-muted-foreground">{error.summary}</p>
        </div>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-medium mb-3">When this happens</h2>
            <ul className="space-y-2">
              {error.when_it_happens.map((item, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="text-muted-foreground select-none">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-medium mb-3">Example response</h2>
            <CodeBlock code={error.example_response.trim()} />
          </section>

          <section>
            <h2 className="text-xl font-medium mb-3">How to fix it</h2>
            <ol className="space-y-2.5">
              {error.how_to_fix.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-muted text-muted-foreground text-xs font-medium flex items-center justify-center select-none">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {related.length > 0 && (
            <section>
              <h2 className="mb-3 font-heading text-xl font-semibold">Related errors</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.type}
                    href={`/errors/${r.type}`}
                    className="surface-card surface-card-hover block p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-heading text-sm font-semibold">{r.title}</span>
                      <Badge variant="outline" className={statusColor(r.status)}>
                        {r.status}
                      </Badge>
                    </div>
                    <code className="mt-2 block font-mono text-xs text-muted-foreground">{r.type}</code>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </PageLayout>
    </>
  )
}
